import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import { Grid } from "@mui/material";
import MDTypography from "components/MDTypography";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import useAlert from "hooks/useAlert";
import AlertMessage from "components/UI/AlertMessage";
import DynamicPieChart from "../charts/piechart";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import MBConfirmationPopup from "components/MBConfirmationPopup";

import Chip from "@mui/material/Chip";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  styled,
  ThemeProvider,
  createTheme,
  Link,
  Alert,
  AlertTitle,
} from "@mui/material";

import Unauthorized from "components/UI/Unauthorized";
import { usePermissions } from "context/PermissionsContext";
import useAPI from "hooks/useAPI";
import PropTypes from "prop-types";

// Custom theme configuration
const theme = createTheme({});
const StatusButton = styled(Button)(({ status }) => ({
  borderRadius: "7px",
  textTransform: "captitalize",
  fontWeight: 500,
  backgroundColor: status === "running" ? "rgba(240,82,82,1)" : "rgba(14,159,110,1)",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  // color: "white",
  padding: "8px 16px",
  letterSpacing: "0.5px",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: status === "running" ? "rgba(240,82,82,0.8)" : "rgba(14,159,110,0.8)",
  },
  padding: "5px 10px",
}));

const StyledButton = styled(Button)(({ status }) => ({
  borderRadius: "7px",
  textTransform: "captitalize",
  fontWeight: 500,
  backgroundColor: "rgb(228, 228, 228)",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
  // color: "white",
  padding: "8px 16px",
  letterSpacing: "0.5px",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "rgb(211, 211, 211)",
  },
  padding: "5px 10px",
  color: "black",
}));

const tableStyles = {
  borderRadius: "8px",
  boxShadow: 3,
  overflow: "hidden",
};

const headerStyles = {
  backgroundColor: "#f5f5f5",
  height: "50%",
  "& .MuiTableCell-root": {
    padding: "8px 16px",
    fontSize: "0.875rem",
  },
};

const WorkerStatus = ({ isIframe = true }) => {
  const initialServicesData = [
    {
      id: 1,
      name: "Global Worker Hub (GWH)",
      key: "GWH",
    },
    {
      id: 2,
      name: "Global Worker Manager (GWM)",
      key: "GWM",
    },
    {
      id: 3,
      name: "Background Worker Handler (BWH)",
      key: "BWH",
    },
    {
      id: 4,
      name: "General Background Worker Monitor (GBWM)",
      key: "GBWM",
    },
    {
      id: 5,
      name: "Kafka Manager (KAFKA)",
      key: "KAFKA",
      detail_page: isIframe ? "/kafka-data" : "/kafka",
    },
    {
      id: 5,
      name: "General Worker Logger (GWL)",
      key: "GWL",
    },
    // {
    //   id: 999,
    //   name: "Others",
    //   key: "others",
    // },
  ];
  const [gunicornData, setGunicornData] = useState(null);
  const { errorState, showAlert } = useAlert();
  const [services, setServices] = useState(initialServicesData);
  const { hasPermission } = usePermissions();
  const { swm_api } = useAPI();
  const [loading, setLoading] = useState(true);
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [serviceName, setServicesName] = useState("");
  const [startingService, setStartingService] = useState(null);
  const [otherProcessesData, setOtherProcessesData] = useState(null);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleDialogOpen = (service) => {
    setDialogOpen(true);
    setServicesName(service);
  };

  const handleDialogClose = (event, reason) => {
    if (reason === "backdropClick") {
      return;
    }
    setDialogOpen(false);
  };

  const requiredPermission = "can_view_smm";
  if (!hasPermission(requiredPermission)) {
    return <Unauthorized />;
  }

  const ColorBar = styled("div")(({ status }) => ({
    width: "0.5rem",
    height: "2.5rem",
    position: "absolute",
    left: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: status === "running" ? "rgba(49,196,141,1)" : "rgba(249,128,128,1)",
    borderRadius: "25px",
  }));

  const handleToggleService = async (name) => {
    setLoadingBtn(true);
    setStartingService(name);
    const service = services.find((s) => s.key === name);

    if (!service) return;

    const { endpoint } =
      service.status === "running"
        ? {
            endpoint: `service_monitor/${name}/stop`,
          }
        : {
            endpoint: `service_monitor/${name}/start`,
          };

    try {
      const { data } = await swm_api.post(endpoint);

      if (data.status === "success") {
        await fetchData();
        showAlert(
          "success",
          t("lang.serviceSuccessMessage", {
            name: name,
            status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
          })
        );
      } else if (data.status === "failed") {
        showAlert(
          "error",
          t("lang.serviceErrorMessage", {
            name: name,
            status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
          })
        );
        return;
      }
    } catch (err) {
      showAlert(
        "error",
        t("lang.serviceErrorMessage", {
          name: name,
          status: service.status === "running" ? t("lang.stopped") : t("lang.started"),
        })
      );
    } finally {
      setLoading(false);
      setLoadingBtn(false);
      handleDialogClose();
      setStartingService(null);
    }
  };

  const fetchData = async () => {
    try {
      const { data } = await swm_api.get(`service_monitor/`);
      setGunicornData(data.data);
      if (data?.others) {
        setOtherProcessesData(data?.others);
      }
    } catch (err) {
      setIsError(true);
      if (err?.response?.data?.error) {
        setErrorMessage(err?.response?.data?.error);
      } else {
        setErrorMessage(err?.message || "Something went wrong, please try again later");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 180000); // Fetch data every 3 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, []);

  useEffect(() => {
    if (gunicornData) {
      setServices((prevServices) =>
        prevServices.map((service) => {
          const serviceData = gunicornData[service.key];
          return serviceData
            ? {
                ...service,
                cpu_usage: `${serviceData.cpu_percent.toFixed(2)}%`,
                memory_usage: `${serviceData.memory_percent.toFixed(2)}%`,
                status: serviceData.status,
              }
            : {
                ...service,
                cpu_usage: "0%",
                memory_usage: "0%",
                status: "stopped",
              };
        })
      );
    }
  }, [gunicornData]);

  const cpuData = gunicornData?.systemstatus?.cpu
    ? [
        { value: gunicornData.systemstatus.cpu.used.toFixed(2), label: t("lang.used") },
        { value: gunicornData.systemstatus.cpu.free.toFixed(2), label: t("lang.free") },
      ]
    : [];

  const memoryData = gunicornData?.systemstatus?.memory
    ? [
        { value: gunicornData.systemstatus.memory.used.toFixed(2), label: t("lang.used") },
        { value: gunicornData.systemstatus.memory.free.toFixed(2), label: t("lang.free") },
      ]
    : [];

  return (
    <>
      {isError ? (
        <Card>
          <Alert
            severity="error"
            sx={{
              color: "rgb(98, 41, 37)",
            }}
          >
            <AlertTitle>{t("lang.error")}</AlertTitle>
            {errorMessage}
          </Alert>
        </Card>
      ) : (
        <>
          <MBConfirmationPopup
            open={dialogOpen}
            handleClose={handleDialogClose}
            handleConfirm={() => handleToggleService(serviceName)}
            title={t("lang.confirmation")}
            message={t("lang.service_stop_confirmation", {
              service: serviceName,
            })}
            confirmText={t("lang.yes_stop")}
            cancelText={t("lang.no_cancel")}
            loadingBtn={loadingBtn}
          />
          <MDBox pt={6} pb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} mb={3}>
                <Card>
                  <MDBox
                    mx={2}
                    mt={-3}
                    py={3}
                    px={2}
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    coloredShadow="info"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <MDTypography variant="h6" color="white">
                      {t("lang.workers")}
                    </MDTypography>
                  </MDBox>
                  <MDBox p={3}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ background: "#ffebee" }}>
                          <MDTypography variant="h6" color="dark" mt={2} ml={2} fontWeight="medium">
                            {t("lang.cpu")}
                            <MDTypography variant="button" ml={1}>
                              {gunicornData?.systemstatus?.cpu ? (
                                `(${gunicornData.systemstatus.cpu.used.toFixed(1)}% ${t(
                                  "lang.used"
                                )})`
                              ) : (
                                <CircularProgress size={15} color="inherit" />
                              )}
                            </MDTypography>
                          </MDTypography>

                          {gunicornData?.systemstatus?.cpu ? (
                            <MDBox p={3}>
                              <DynamicPieChart
                                height={150}
                                colors={["#1976d2", "#4caf50", "#ff9800", "#e91e63", "#9c27b0"]}
                                data={cpuData}
                                itemNb={2}
                                radius={20}
                                skipAnimation={false}
                                valueFormatter={(value) => `${value}%`}
                              />
                            </MDBox>
                          ) : (
                            <MDBox p={3}>
                              <Skeleton height={150} />
                            </MDBox>
                          )}
                        </Card>
                      </Grid>

                      {/* Memory Section */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ background: "#e1f5fe" }}>
                          <MDTypography variant="h6" color="dark" mt={2} ml={2} fontWeight="medium">
                            {t("lang.memory")}
                            <MDTypography variant="button" ml={1}>
                              {gunicornData?.systemstatus?.memory ? (
                                `(${gunicornData.systemstatus.memory.used.toFixed(1)}% ${t(
                                  "lang.used"
                                )})`
                              ) : (
                                <CircularProgress size={15} color="inherit" />
                              )}
                            </MDTypography>
                          </MDTypography>

                          {gunicornData?.systemstatus?.memory ? (
                            <MDBox p={3}>
                              <DynamicPieChart
                                height={150}
                                data={memoryData}
                                itemNb={2}
                                radius={20}
                                skipAnimation={false}
                                valueFormatter={(value) => `${value}%`}
                              />
                            </MDBox>
                          ) : (
                            <MDBox p={3}>
                              <Skeleton height={150} />
                            </MDBox>
                          )}
                        </Card>
                      </Grid>
                    </Grid>
                  </MDBox>

                  <MDBox p={3}>
                    <ThemeProvider theme={theme}>
                      <TableContainer component={Paper} sx={tableStyles}>
                        <Table>
                          <TableHead>
                            <TableRow sx={headerStyles}>
                              <TableCell></TableCell>
                              <TableCell>{t("lang.name")}</TableCell>
                              <TableCell>
                                {t("lang.cpu")} {t("lang.usage")}
                              </TableCell>
                              <TableCell>
                                {t("lang.memory")} {t("lang.usage")}
                              </TableCell>
                              <TableCell align="center">
                                {hasPermission("can_manage_smm") ? " " : t("lang.status")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {services.map((service) => (
                              <TableRow key={service.id} sx={{ position: "relative" }}>
                                <TableCell
                                  sx={{
                                    position: "relative",
                                    padding: "0 16px",
                                    width: "24px",
                                  }}
                                >
                                  <ColorBar status={service.status} />
                                </TableCell>

                                <TableCell sx={{ fontWeight: "500" }}>{service.name}</TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>
                                  {loading ? <Skeleton /> : service.cpu_usage}
                                </TableCell>
                                <TableCell sx={{ fontWeight: "500" }}>
                                  {loading ? <Skeleton /> : service.memory_usage}
                                </TableCell>
                                <TableCell align="center">
                                  {service.key != "others" && (
                                    <>
                                      {hasPermission("can_manage_smm") ? (
                                        loading ? (
                                          <Skeleton />
                                        ) : (
                                          <MDBox display="flex" justifyContent="flex-end" gap={1}>
                                            {service.detail_page && (
                                              <Link href={service.detail_page}>
                                                <StyledButton>{t("Details")}</StyledButton>
                                              </Link>
                                            )}
                                            <StatusButton
                                              status={service.status}
                                              sx={{
                                                color: "white",
                                                "&.Mui-disabled": {
                                                  color: "white",
                                                  opacity: 0.5,
                                                },
                                              }}
                                              onClick={
                                                service.status === "running"
                                                  ? () => handleDialogOpen(service.key)
                                                  : () => handleToggleService(service.key)
                                              }
                                              disabled={startingService === service.key}
                                              startIcon={
                                                startingService === service.key ? (
                                                  <CircularProgress
                                                    size={18}
                                                    sx={{ color: "white" }}
                                                  />
                                                ) : null
                                              }
                                            >
                                              {service.status === "running"
                                                ? t("lang.stop")
                                                : t("lang.start")}
                                            </StatusButton>
                                          </MDBox>
                                        )
                                      ) : service.status === "running" ? (
                                        loading ? (
                                          <Skeleton />
                                        ) : (
                                          <Chip
                                            label={t("lang.running")}
                                            sx={{
                                              backgroundColor: "rgba(49,196,141,1)",
                                              color: "white",
                                              fontWeight: "500",
                                            }}
                                          />
                                        )
                                      ) : loading ? (
                                        <Skeleton />
                                      ) : (
                                        <Chip
                                          label={t("lang.stopped")}
                                          sx={{
                                            backgroundColor: "rgba(249,128,128,1)",
                                            color: "white",
                                            fontWeight: "500",
                                          }}
                                        />
                                      )}
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </ThemeProvider>
                  </MDBox>
                </Card>
              </Grid>
            </Grid>
          </MDBox>
          <AlertMessage {...errorState} />
        </>
      )}
    </>
  );
};

WorkerStatus.propTypes = {
  isIframe: PropTypes.bool,
};

WorkerStatus.defaultProps = {
  isIframe: true,
};

export default WorkerStatus;
